import { createContext, useContext, useEffect, useState } from 'react';
import { useScroll } from 'framer-motion';

const DepthContext = createContext({ depth: 0, scrollProgress: 0 });

export function useOceanDepth() {
  return useContext(DepthContext);
}

export function OceanDepthProvider({ children }) {
  const { scrollYProgress } = useScroll();
  const [depth, setDepth] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    return scrollYProgress.on('change', (latest) => {
      setScrollProgress(latest);

      // Convert scroll progress to depth zones
      // 0-0.15: surface
      // 0.15-0.35: shallow
      // 0.35-0.6: midwater
      // 0.6-0.85: deep
      // 0.85-1: deepest

      if (latest < 0.15) {
        setDepth('surface');
      } else if (latest < 0.35) {
        setDepth('shallow');
      } else if (latest < 0.6) {
        setDepth('midwater');
      } else if (latest < 0.85) {
        setDepth('deep');
      } else {
        setDepth('deepest');
      }
    });
  }, [scrollYProgress]);

  return (
    <DepthContext.Provider value={{ depth, scrollProgress }}>
      {children}
    </DepthContext.Provider>
  );
}

// Get background color based on depth
export function getDepthColor(scrollProgress) {
  // Surface: deep blue/teal navy #0a1628
  // Shallow: darker ocean blue
  // Midwater: deep navy
  // Deep: near-black blue

  if (scrollProgress < 0.15) {
    // Surface
    return 'linear-gradient(to bottom, #0a1628 0%, #0d1a2d 50%, #1a3a52 100%)';
  } else if (scrollProgress < 0.35) {
    // Shallow - transitioning darker
    const t = (scrollProgress - 0.15) / 0.2;
    return `linear-gradient(to bottom,
      hsl(${215 - t * 5}, ${50 - t * 10}%, ${6 - t * 2}%) 0%,
      hsl(${210 - t * 5}, ${45 - t * 10}%, ${10 - t * 3}%) 50%,
      hsl(${210 - t * 10}, ${40 - t * 10}%, ${20 - t * 8}%) 100%)`;
  } else if (scrollProgress < 0.6) {
    // Midwater - deep navy
    const t = (scrollProgress - 0.35) / 0.25;
    return `linear-gradient(to bottom,
      hsl(${210 - t * 10}, ${40 - t * 10}%, ${4 - t * 1}%) 0%,
      hsl(${205 - t * 10}, ${35 - t * 10}%, ${7 - t * 2}%) 50%,
      hsl(${200 - t * 10}, ${30 - t * 10}%, ${12 - t * 4}%) 100%)`;
  } else if (scrollProgress < 0.85) {
    // Deep - very dark
    const t = (scrollProgress - 0.6) / 0.25;
    return `linear-gradient(to bottom,
      hsl(${200 - t * 10}, ${30 - t * 15}%, ${3 - t * 1}%) 0%,
      hsl(${195 - t * 10}, ${25 - t * 15}%, ${5 - t * 2}%) 50%,
      hsl(${190 - t * 10}, ${20 - t * 15}%, ${8 - t * 3}%) 100%)`;
  } else {
    // Deepest - near black
    return `linear-gradient(to bottom,
      hsl(190, 15%, 2%) 0%,
      hsl(185, 10%, 3%) 50%,
      hsl(180, 10%, 5%) 100%)`;
  }
}

// Get light intensity based on depth (for moon reflection)
export function getLightIntensity(scrollProgress) {
  if (scrollProgress < 0.15) {
    return 1; // Full intensity at surface
  } else if (scrollProgress < 0.35) {
    // Fade through shallow water
    const t = (scrollProgress - 0.15) / 0.2;
    return 1 - t * 0.4; // Down to 60%
  } else if (scrollProgress < 0.6) {
    // Fade through midwater
    const t = (scrollProgress - 0.35) / 0.25;
    return 0.6 - t * 0.4; // Down to 20%
  } else if (scrollProgress < 0.85) {
    // Almost gone in deep water
    const t = (scrollProgress - 0.6) / 0.25;
    return 0.2 - t * 0.15; // Down to 5%
  } else {
    // Nearly invisible in deepest
    return 0.05;
  }
}

// Get particle density based on depth
export function getParticleDensity(scrollProgress) {
  if (scrollProgress < 0.15) {
    return 0; // No particles at surface
  } else if (scrollProgress < 0.35) {
    // Begin appearing in shallow
    const t = (scrollProgress - 0.15) / 0.2;
    return t * 15; // Up to 15 particles
  } else if (scrollProgress < 0.6) {
    // Peak in midwater
    return 15 + ((scrollProgress - 0.35) / 0.25) * 10; // Up to 25
  } else if (scrollProgress < 0.85) {
    // Reduce in deep
    const t = (scrollProgress - 0.6) / 0.25;
    return 25 - t * 15; // Down to 10
  } else {
    // Sparse in deepest
    return 10;
  }
}
